'use client';

import { useState, useEffect, useRef, useCallback, useTransition } from 'react';

import { StepData } from '@/types/restResponses/steps';
import { isCommentHash, parseCommentHash } from '@/utils/hashChange/utils';

interface UseHashNavigationParams {
  steps: StepData[];
  activeStep: number;
  setActiveStep: (stepIndex: number) => void;
  setActivePhoto: (photoIndex: number) => void;
  replaceWithHash: (hash: string, options?: { scroll?: boolean }) => void;
  isNavigating?: boolean;
}

/**
 * Unified hook for hash change detection and hash-based navigation
 * Eliminates race conditions by handling both concerns in a single hook
 */
export function useHashNavigation({
  steps,
  activeStep,
  setActiveStep,
  setActivePhoto,
  replaceWithHash,
  isNavigating = false,
}: UseHashNavigationParams) {
  const [_, startTransition] = useTransition();
  
  const getHash = () => {
    const hash = typeof window !== 'undefined' ? decodeURIComponent(window.location.hash).replace('#', '') : '';
    return hash;
  };

  const [currentHash, setCurrentHash] = useState(() => {
    const initialHash = getHash();
    return initialHash;
  });

  // Use ref to store current hash for immediate access without dependency issues
  const hashRef = useRef(currentHash);
  hashRef.current = currentHash;

  // Optimized update function that checks for changes immediately
  const updateHashFromUrl = useCallback(() => {
    const newHash = getHash();
    if (newHash !== hashRef.current) {
      setCurrentHash(newHash);
      hashRef.current = newHash;
    }
  }, []);

  // Hash change detection effect
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Listen for hash changes
      const handleHashChange = () => {
        updateHashFromUrl();
      };

      // Listen for Next.js route changes (router.push doesn't trigger hashchange)
      const handlePopState = () => {
        updateHashFromUrl();
      };

      // Much more aggressive polling for immediate sync (every 10ms)
      const pollInterval = setInterval(updateHashFromUrl, 10);

      // Use requestAnimationFrame for even faster sync on next paint
      let rafId: number;
      const rafSync = () => {
        updateHashFromUrl();
        rafId = requestAnimationFrame(rafSync);
      };
      rafId = requestAnimationFrame(rafSync);

      window.addEventListener('hashchange', handleHashChange);
      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('hashchange', handleHashChange);
        window.removeEventListener('popstate', handlePopState);
        clearInterval(pollInterval);
        cancelAnimationFrame(rafId);
      };
    }
  }, []); // Remove currentHash dependency to prevent effect recreation

  // Navigation logic effect - only runs when NOT navigating
  useEffect(() => {
    // Skip all hash navigation if we're currently navigating
    if (isNavigating) {
      return;
    }

    if (currentHash === 'last' && steps?.length) {
      // Set to last step without adding to history
      const lastStepIndex = steps.length - 1;
      
      startTransition(() => {
        setActiveStep(lastStepIndex);
        if (steps[lastStepIndex]?.media?.gallery) {
          setActivePhoto(steps[lastStepIndex].media.gallery.length - 1);
        }
      });
      
      replaceWithHash(steps[lastStepIndex]?.id?.toString() || '', {
        scroll: false,
      });
      return;
    }

    // Handle comment hashes (format: comment-{stepId}-{commentId})
    if (isCommentHash(currentHash) && steps?.length) {
      const commentData = parseCommentHash(currentHash);
      if (commentData) {
        const stepId = parseInt(commentData.commentId); // First ID is the step ID
        const stepIndex = steps.findIndex(step => step.id === stepId);
        
        if (stepIndex !== -1 && stepIndex !== activeStep) {
          // Navigate to the step that contains the comment
          startTransition(() => {
            setActiveStep(stepIndex);
          });
          // Keep the comment hash so the comment can be highlighted and scrolled to
          // Don't call replaceWithHash here - keep the original comment hash
        }
      }
      return;
    }

    // default_id is used when changing language to find the equivalent step in the new language
    if (currentHash !== 'last' && steps?.length) {
      const hash = parseInt(currentHash);
      const stepIndex = steps.findIndex(step => step.id === hash);

      // If we can't find a step with this ID, look for a step with this default_id
      if (stepIndex === -1) {
        const defaultStepIndex = steps.findIndex(step => step.default_id === hash);
        if (defaultStepIndex !== -1) {
          startTransition(() => {
            setActiveStep(defaultStepIndex);
          });
          replaceWithHash(steps[defaultStepIndex].id.toString());
        }
      } else if (stepIndex !== activeStep) {
        // If we found a step by ID and it's not already active
        startTransition(() => {
          setActiveStep(stepIndex);
        });
      }
    }
  }, [currentHash, steps, activeStep, setActiveStep, setActivePhoto, replaceWithHash, isNavigating]);

  return currentHash;
}